import React, { useState, useEffect } from "react";
import { api, auth } from "@/lib/api";
import { setupPrivateNoteKey, clearPrivateNoteKey } from "@/lib/privateCrypto";
import { COUNTRIES, detectCountry } from "@/lib/countries";
import { toast } from "sonner";
import { Copy, Check } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { isWeakPin, WEAK_PIN_MESSAGE } from "@/lib/pinStrength";
import { setSettingsPinRequired } from "@/lib/settingsPinLock";
import { useI18n } from "@/lib/i18n";

export default function Onboarding({ onReady }) {
  const { t } = useI18n();
  const [mode, setMode] = useState("join"); // join | create
  const [busy, setBusy] = useState(false);

  // join form
  const [joinCode, setJoinCode] = useState("");
  const [joinPin, setJoinPin] = useState("");

  // create form
  const [familyName, setFamilyName] = useState("");
  const [createPin, setCreatePin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [country, setCountry] = useState(() => detectCountry() || "");

  // post-create "share this code" screen
  const [createdFamily, setCreatedFamily] = useState(null);
  const [copied, setCopied] = useState(false);

  // Terms & Privacy acceptance — required before joining or creating a family.
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Auto-fill join code from ?join=XXX URL param (from QR scan)
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const url = new URL(window.location.href);
      const code = (url.searchParams.get("join") || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
      if (code) {
        setJoinCode(code.slice(0, 8));
        setMode("join");
        return;
      }
      if (url.searchParams.get("mode") === "create") {
        setMode("create");
      }
    } catch {}
  }, []);

  const handleJoin = async (e) => {
    e?.preventDefault();
    if (joinCode.trim().length < 4) {
      toast.error("Enter your join code");
      return;
    }
    if (joinPin.length < 4) {
      toast.error("Enter your family PIN");
      return;
    }
    if (!acceptedTerms) {
      toast.error("Please accept the Terms and Privacy Policy");
      return;
    }
    setBusy(true);
    try {
      const res = await api.joinFamily({
        join_code: joinCode.trim().toUpperCase(),
        pin: joinPin.trim(),
      });
      auth.setSession({
        token: res.token,
        family_id: res.family_id,
        name: res.name,
      });
      // Brand-new device on this family — don't ask for the PIN to
      // open Settings. Parents expect to wander in and tweak things
      // straight after joining; the family PIN is already protecting
      // the join itself. Users who want extra friction (shared
      // iPads etc.) can flip it back on in Settings → Family.
      setSettingsPinRequired(false);
      // Derive the private-note encryption key while we still have the PIN
      await setupPrivateNoteKey(joinPin.trim(), res.family_id);
      toast.success(`Welcome back to ${res.name}`);
      onReady();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not join");
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async (e) => {
    e?.preventDefault();
    if (!familyName.trim()) {
      toast.error("Family name required");
      return;
    }
    if (createPin.length < 6 || !/^\d+$/.test(createPin)) {
      toast.error("PIN must be at least 6 digits");
      return;
    }
    if (isWeakPin(createPin)) {
      toast.error(WEAK_PIN_MESSAGE);
      return;
    }
    if (createPin !== confirmPin) {
      toast.error("PINs don’t match");
      return;
    }
    if (!acceptedTerms) {
      toast.error("Please accept the Terms and Privacy Policy");
      return;
    }
    setBusy(true);
    try {
      const res = await api.createFamily({
        name: familyName.trim(),
        pin: createPin,
        country: country || null,
      });
      auth.setSession({
        token: res.token,
        family_id: res.family_id,
        name: res.name,
      });
      // Brand-new family — keep the Settings PIN gate off by default
      // so the creator can sail through setup. They can turn it on
      // any time from Settings → Family.
      setSettingsPinRequired(false);
      // Flag that this device just birthed the account. Dashboard reads
      // this once and auto-opens the Welcome quick guide, then clears it.
      // Using localStorage so a refresh/tab-close between Onboarding and
      // Dashboard doesn't swallow the guide.
      try {
        localStorage.setItem("famfeel_new_account_v1", "1");
      } catch {
        /* quota/privacy-mode — non-fatal, guide is still re-openable from Settings */
      }
      // Private-note encryption key derived from PIN + family_id — stays on
      // this device only, server never sees it.
      await setupPrivateNoteKey(createPin, res.family_id);
      setCreatedFamily(res);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not create family");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    if (!createdFamily?.join_code) return;
    try {
      await navigator.clipboard.writeText(createdFamily.join_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.info("Copy the code manually");
    }
  };

  if (createdFamily) {
    const joinUrl = `${window.location.origin}/?join=${encodeURIComponent(
      createdFamily.join_code
    )}`;
    return (
      <div
        className="min-h-screen px-6 py-12 md:py-20 flex items-start justify-center"
        data-testid="onboarding-created"
      >
        <div className="max-w-lg w-full">
          <div className="label-tiny mb-3">
            {t({ en: "Family created · FamFeel", da: "Familie oprettet · FamFeel" })}
          </div>
          <h1 className="font-display-italic text-4xl md:text-5xl leading-[1.05] mb-4">
            {t({ en: "Welcome,", da: "Velkommen," })} {createdFamily.name}
          </h1>
          <p className="text-lg text-[color:var(--ink-2)] mb-8">
            {t({
              en: "Scan the QR from another phone — or share the code + PIN manually. Each device enters them once, then stays signed in.",
              da: "Scan QR-koden fra en anden telefon — eller del koden + PIN manuelt. Hver enhed indtaster dem én gang og forbliver logget ind.",
            })}
          </p>

          <div className="ff-card p-6 space-y-6">
            <div className="flex items-center justify-center bg-white rounded-xl p-5 border border-[color:var(--line-soft)]">
              <QRCodeSVG
                value={joinUrl}
                size={200}
                level="M"
                includeMargin={false}
                data-testid="join-qr"
              />
            </div>
            <div>
              <div className="label-tiny mb-2">
                {t({ en: "Join code", da: "Familie-kode" })}
              </div>
              <div className="flex items-center gap-3">
                <div
                  className="font-display text-4xl tabular tracking-widest flex-1 select-all"
                  data-testid="created-join-code"
                >
                  {createdFamily.join_code}
                </div>
                <button
                  onClick={copyCode}
                  className="ff-btn"
                  data-testid="copy-join-code"
                  aria-label={t({ en: "Copy code", da: "Kopiér kode" })}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" strokeWidth={2} /> {t({ en: "Copied", da: "Kopieret" })}
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" strokeWidth={2} /> {t({ en: "Copy", da: "Kopiér" })}
                    </>
                  )}
                </button>
              </div>
            </div>
            <div>
              <div className="label-tiny mb-2">
                {t({ en: "Family PIN", da: "Familie-PIN" })}
              </div>
              <p className="text-[15px] text-[color:var(--ink)]">
                {t({
                  en: "The PIN you just set. Don't share publicly — it keeps your family private. The QR only contains the code, so the PIN still needs to be said out loud or typed.",
                  da: "PIN-koden du lige valgte. Del den ikke offentligt — den holder familien privat. QR-koden indeholder kun koden, så PIN skal stadig siges højt eller tastes ind.",
                })}
              </p>
            </div>
          </div>

          <button
            onClick={onReady}
            className="ff-btn ff-btn-primary w-full mt-8 text-lg py-4"
            data-testid="onboarding-continue"
          >
            {t({ en: "Set up family members →", da: "Opret familiemedlemmer →" })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen px-6 py-12 md:py-20 flex items-start justify-center"
      data-testid="onboarding-page"
    >
      <div className="max-w-lg w-full">
        <div className="label-tiny mb-3">FamFeel</div>
        <h1 className="font-display-italic text-5xl md:text-6xl leading-[1.05] mb-4">
          {mode === "join"
            ? t({ en: "Join your family", da: "Deltag i din familie" })
            : t({ en: "Start a new family", da: "Start en ny familie" })}
        </h1>
        <p className="text-lg text-[color:var(--ink-2)] mb-8">
          {mode === "join"
            ? t({
                en: "Enter the join code and PIN from whoever set up your family.",
                da: "Indtast familie-koden og PIN fra den, der oprettede familien.",
              })
            : t({
                en: "Pick a name and a 6-digit PIN. You'll get a join code to share with everyone else.",
                da: "Vælg et navn og en 6-cifret PIN. Du får en familie-kode, du kan dele med resten.",
              })}
        </p>

        <div className="flex gap-1 mb-6 border-b border-[color:var(--line)]">
          <button
            onClick={() => setMode("join")}
            className={`px-4 py-2 label-tiny border-b-2 transition-colors ${
              mode === "join"
                ? "border-[color:var(--ink)] text-[color:var(--ink)]"
                : "border-transparent hover:text-[color:var(--ink)]"
            }`}
            data-testid="tab-join"
          >
            {t({ en: "Have a code", da: "Har en kode" })}
          </button>
          <button
            onClick={() => setMode("create")}
            className={`px-4 py-2 label-tiny border-b-2 transition-colors ${
              mode === "create"
                ? "border-[color:var(--ink)] text-[color:var(--ink)]"
                : "border-transparent hover:text-[color:var(--ink)]"
            }`}
            data-testid="tab-create"
          >
            {t({ en: "Create new", da: "Opret ny" })}
          </button>
        </div>

        {mode === "join" ? (
          <form onSubmit={handleJoin} className="space-y-5">
            <div>
              <label className="label-tiny block mb-2">
                {t({ en: "Join code", da: "Familie-kode" })}
              </label>
              <input
                type="text"
                value={joinCode}
                onChange={(e) =>
                  setJoinCode(
                    e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 8)
                  )
                }
                placeholder={t({ en: "e.g. H4NSEN", da: "fx H4NSEN" })}
                className="ff-input tabular tracking-widest text-center text-2xl font-display"
                data-testid="join-code-input"
                autoFocus
                inputMode="text"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck="false"
              />
            </div>
            <div>
              <label className="label-tiny block mb-2">
                {t({ en: "Family PIN", da: "Familie-PIN" })}
              </label>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={12}
                value={joinPin}
                onChange={(e) =>
                  setJoinPin(e.target.value.replace(/\D/g, ""))
                }
                placeholder={t({ en: "your family PIN", da: "din familie-PIN" })}
                className="ff-input font-mono tracking-widest text-center text-2xl"
                data-testid="join-pin-input"
              />
            </div>
            <label
              className="flex items-start gap-3 cursor-pointer select-none pt-1"
              data-testid="join-terms-label"
            >
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 w-5 h-5 accent-[color:var(--ink)] cursor-pointer flex-shrink-0"
                data-testid="join-terms-checkbox"
              />
              <span className="text-[14px] leading-snug text-[color:var(--ink-2)]">
                {t({ en: "I agree to FamFeel's", da: "Jeg accepterer FamFeels" })}{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-[color:var(--ink)]"
                  data-testid="join-terms-link"
                >
                  {t({ en: "Terms", da: "Vilkår" })}
                </a>{" "}
                {t({ en: "and", da: "og" })}{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-[color:var(--ink)]"
                  data-testid="join-privacy-link"
                >
                  {t({ en: "Privacy Policy", da: "Privatlivspolitik" })}
                </a>
                .
              </span>
            </label>
            <button
              type="submit"
              disabled={busy}
              className="ff-btn ff-btn-primary w-full text-lg py-4"
              data-testid="join-submit"
            >
              {busy
                ? t({ en: "Checking…", da: "Tjekker…" })
                : t({ en: "Open family →", da: "Åbn familie →" })}
            </button>
          </form>
        ) : (
          <form onSubmit={handleCreate} className="space-y-5">
            <div>
              <label className="label-tiny block mb-2">
                {t({ en: "Family name", da: "Familienavn" })}
              </label>
              <input
                type="text"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                placeholder={t({ en: "e.g. The Hansens", da: "fx Familien Hansen" })}
                className="ff-input"
                data-testid="create-name-input"
                autoFocus
                maxLength={40}
              />
            </div>
            <div>
              <label className="label-tiny block mb-2">
                {t({ en: "Choose a family PIN (6+ digits)", da: "Vælg en familie-PIN (6+ cifre)" })}
              </label>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={12}
                value={createPin}
                onChange={(e) =>
                  setCreatePin(e.target.value.replace(/\D/g, ""))
                }
                placeholder="●●●●●●"
                className="ff-input font-mono tracking-widest text-center text-2xl"
                data-testid="create-pin-input"
              />
            </div>
            <div>
              <label className="label-tiny block mb-2">
                {t({ en: "Confirm PIN", da: "Bekræft PIN" })}
              </label>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={12}
                value={confirmPin}
                onChange={(e) =>
                  setConfirmPin(e.target.value.replace(/\D/g, ""))
                }
                placeholder="●●●●●●"
                className="ff-input font-mono tracking-widest text-center text-2xl"
                data-testid="create-pin-confirm"
              />
            </div>
            <div>
              <label className="label-tiny block mb-2">
                {t({ en: "Country", da: "Land" })}{" "}
                <span className="text-[color:var(--ink-3)]">
                  {t({
                    en: "(optional — helps us know where FamFeel's loved)",
                    da: "(valgfrit — så vi ved hvor FamFeel er elsket)",
                  })}
                </span>
              </label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="ff-input"
                data-testid="create-country-select"
              >
                <option value="">{t({ en: "Prefer not to say", da: "Foretrækker at lade være" })}</option>
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <label
              className="flex items-start gap-3 cursor-pointer select-none pt-1"
              data-testid="create-terms-label"
            >
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 w-5 h-5 accent-[color:var(--ink)] cursor-pointer flex-shrink-0"
                data-testid="create-terms-checkbox"
              />
              <span className="text-[14px] leading-snug text-[color:var(--ink-2)]">
                {t({ en: "I agree to FamFeel's", da: "Jeg accepterer FamFeels" })}{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-[color:var(--ink)]"
                  data-testid="create-terms-link"
                >
                  {t({ en: "Terms", da: "Vilkår" })}
                </a>{" "}
                {t({ en: "and", da: "og" })}{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-[color:var(--ink)]"
                  data-testid="create-privacy-link"
                >
                  {t({ en: "Privacy Policy", da: "Privatlivspolitik" })}
                </a>
                .
              </span>
            </label>
            <button
              type="submit"
              disabled={busy || !acceptedTerms}
              className="ff-btn ff-btn-primary w-full text-lg py-4 disabled:opacity-50"
              data-testid="create-submit"
            >
              {busy
                ? t({ en: "Creating…", da: "Opretter…" })
                : t({ en: "Create family →", da: "Opret familie →" })}
            </button>
          </form>
        )}

        <p className="label-tiny mt-10 text-[color:var(--ink-3)]">
          {t({
            en: "No accounts, no email. One shared PIN per family. Each device stays signed in — you can sign out anytime in Settings.",
            da: "Ingen konti, ingen e-mail. Én fælles PIN pr. familie. Hver enhed forbliver logget ind — log ud når du vil i Indstillinger.",
          })}
        </p>
      </div>
    </div>
  );
}
